// @flow
import { COMPLETE_ACCOUNT_REQUEST, COMPLETE_ACCOUNT_SUCCESS } from './completeAccount';
import { CREATE_ACCOUNT_FAIL, CREATE_ACCOUNT_REQUEST, CREATE_ACCOUNT_SUCCESS } from './createAccount';
import type { GeoCoordinates, UserData } from '../../types/UserData';
import { LOGIN_FAIL, LOGIN_REQUEST, LOGIN_SUCCESS } from './login';
import { PASSWORD_RESET_REQUEST, PASSWORD_RESET_SUCCESS } from './passwordReset';
import { UPDATE_ACCOUNT_REQUEST, UPDATE_ACCOUNT_SUCCESS } from './updateAccount';
import { CONFIRM_PASSWORD_SUCCESS } from './confirmPassword';
import Cookies from 'universal-cookie';
import { createSelector } from 'reselect';
import { fetchBidderData } from '../api/bidder';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import { HYDRATE_CLIENT_STATE } from './client';
import jwtdecode from 'jwt-decode';
import type { MainhostLogin } from '../../types/MainhostLogin';
import ms from 'ms';
import { OPEN_CONFIRM_PASSWORD_MODAL_FROM_CHECKER } from './shouldLogin';
import { SUBMIT_CONSENT_SUCCESS } from './consent';
import uuid from 'uuid';

const cookie = new Cookies();

/* Action Types */
export const LOAD_BIDDER_DATA_FAIL = 'la/domain/bidderData/LOAD_FAIL';
export const LOAD_BIDDER_DATA_REQUEST = 'la/domain/bidderData/LOAD_REQUEST';
export const LOAD_BIDDER_DATA_SUCCESS = 'la/domain/bidderData/LOAD_SUCCESS';
export const LOG_OUT_BIDDER = 'LOG_OUT_BIDDER';

const REDUX_STORE_TIME = ms('30m');
const UNREGISTERED = 'unregistered';
const PARTIALLYREGISTEREDBIDDER = 'PartiallyRegisteredBidder';
const REGISTEREDBIDDER = 'bidder';

/* reducer */
const DEFAULT_STATE = {
    authName: '',
    authNuid: '',
    authVerify: '',
    confirmPasswordRequiredTriggered: false,
    loaded: 0,
    loading: false,
    mobile: '',
    token: '',
    userData: {},
};

export type State = {|
    ...typeof DEFAULT_STATE,
    userData: UserData | {},
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let time;

    switch (action.type) {
        case CREATE_ACCOUNT_SUCCESS:
            time = action.meta.actionTime;
            return {
                ...state,
                loaded: time,
                loading: false,
                token: action.payload,
            };
        case LOGIN_SUCCESS:
        case CONFIRM_PASSWORD_SUCCESS:
        case COMPLETE_ACCOUNT_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
            time = action.meta.actionTime;
            if (typeof action.payload.annualNMS !== 'undefined') {
                action.payload.bidder.ANMS = action.payload.annualNMS;
            }
            return {
                ...state,
                authName: action.payload.mainhostLogin['5Xq_name'],
                authNuid: action.payload.mainhostLogin['5Xq_nuid'],
                authVerify: action.payload.mainhostLogin['5Xq_nverify'],
                confirmPasswordRequiredTriggered: false,
                loaded: time,
                loading: false,
                token: action.payload.token,
                userData: {
                    ...action.payload.bidder,
                    bidderId: Number(action.payload.bidder.bidderId),
                },
            };
        case LOAD_BIDDER_DATA_REQUEST:
        case LOGIN_REQUEST:
        case CREATE_ACCOUNT_REQUEST:
        case COMPLETE_ACCOUNT_REQUEST:
        case PASSWORD_RESET_REQUEST:
        case UPDATE_ACCOUNT_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_BIDDER_DATA_SUCCESS:
        case UPDATE_ACCOUNT_SUCCESS:
            time = action.meta.actionTime;
            if (typeof action.payload.annualNMS !== 'undefined') {
                action.payload.bidder.ANMS = action.payload.annualNMS;
            }
            return {
                ...state,
                loaded: time,
                loading: false,
                userData: {
                    ...action.payload.bidder,
                    bidderId: Number(action.payload.bidder.bidderId),
                },
            };
        case OPEN_CONFIRM_PASSWORD_MODAL_FROM_CHECKER:
            return {
                ...state,
                confirmPasswordRequiredTriggered: true,
            };
        case CREATE_ACCOUNT_FAIL:
        case LOG_OUT_BIDDER:
        case LOGIN_FAIL:
            return DEFAULT_STATE;
        case SUBMIT_CONSENT_SUCCESS:
            return {
                ...state,
                userData: {
                    ...state.userData,
                    acceptedTermsOfService: action.payload,
                },
            };
        case HYDRATE_CLIENT_STATE:
            return {
                ...state,
                authName: action.payload.authName,
                authNuid: action.payload.authNuid,
                authVerify: action.payload.authVerify,
                token: action.payload.token,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.user;

export const userDataSelector: GlobalState => UserData = createSelector(stateSelector, state => state.userData);

export const getANMS: GlobalState => number = createSelector(userDataSelector, userData => {
    return userData.ANMS ? userData.ANMS : 0;
});

export const getAuthToken: GlobalState => string = createSelector(stateSelector, state => state.token);

export const getBidderEmail: GlobalState => string = createSelector(userDataSelector, userData => {
    return userData.email ? userData.email : '';
});

export const getCreditCardValidated: GlobalState => boolean = createSelector(userDataSelector, userData => {
    return userData.creditCardValidated ? userData.creditCardValidated : false;
});

export const isTermsOfServiceUnknown: GlobalState => boolean = createSelector(userDataSelector, userData => {
    return userData.acceptedTermsOfService === 'unknown';
});

export const getBidderCountry: GlobalState => string = createSelector(userDataSelector, userData => {
    return userData.country ? userData.country : '';
});

export const getBidderState: GlobalState => string = createSelector(userDataSelector, userData => {
    return userData.state ? userData.state : '';
});

export const isBidderInContiguousUS: GlobalState => boolean = createSelector(
    [getBidderCountry, getBidderState],
    (country, state) => {
        const nonContiguousState = [
            'Hawaii',
            'HI',
            'Alaska',
            'AK',
            'American Samoa',
            'AS',
            'Virgin Islands',
            'VI',
            'Northern Mariana Islands',
            'MP',
            'Guam',
            'GU',
            'Puerto Rico',
            'PR',
        ];
        return ['United States', 'US'].includes(country) && !nonContiguousState.includes(state);
    },
);

export const getBidderType: GlobalState => string = createSelector(getAuthToken, token => {
    if (token) {
        try {
            // TODO: see if this try catch is enough.  We shouldn't throw if something is wrong
            return jwtdecode(token).type;
        } catch (error) {
            return UNREGISTERED;
        }
    }
    return UNREGISTERED;
});

export const getBidderIdFromToken: GlobalState => number = createSelector(getAuthToken, token => {
    if (token) {
        try {
            // TODO: see if this try catch is enough.  We shouldn't throw if something is wrong
            const decoded = jwtdecode(token);
            if (decoded.type === 'bidder') {
                return decoded.sub;
            }
        } catch (error) {
            return -1;
        }
    }
    return -1;
});

export const isConfirmPasswordTriggered: GlobalState => boolean = createSelector(
    stateSelector,
    state => state.confirmPasswordRequiredTriggered,
);

export const isUserPartiallyRegistered: GlobalState => boolean = createSelector(getBidderType, bidderType => {
    return bidderType === PARTIALLYREGISTEREDBIDDER;
});

const isUserRegistered: GlobalState => boolean = createSelector(getBidderType, bidderType => {
    return bidderType === REGISTEREDBIDDER;
});

export const isUserLoggedIn: GlobalState => boolean = createSelector(
    getAuthToken,
    isUserRegistered,
    (token, registered) => {
        return Boolean(token) && registered;
    },
);

export const isUserAtLeastPartiallyRegistered: GlobalState => boolean = createSelector(
    isUserPartiallyRegistered,
    isUserLoggedIn,
    (registered, loggedIn) => registered || loggedIn,
);

export const getUserData: GlobalState => UserData = createSelector(
    isUserAtLeastPartiallyRegistered,
    userDataSelector,
    (userAtLeastPartiallyRegistered, userData) => {
        return userAtLeastPartiallyRegistered ? userData : {};
    },
);

export const getUserIsInGoodStanding: GlobalState => boolean = createSelector(getUserData, userData => {
    const standing = userData.standing || '';
    return standing === '' || standing === 'y' || standing === 'good';
});

export const getBidderIsSuspended: GlobalState => boolean = createSelector(getUserData, userData => {
    const standing = userData.standing || '';
    return standing === 's';
});

export const getUserFullName: GlobalState => string = createSelector(getUserData, userData => {
    const first = userData.firstName || '';
    const last = userData.lastName || '';
    return `${first} ${last}`.trim();
});

export const getUserFirstName: GlobalState => string = createSelector(getUserData, userData => {
    const first = userData.firstName || '';
    return first.trim();
});

export const getUserHasCardOnFile: GlobalState => boolean = createSelector(getUserData, userData => {
    return Boolean(userData.creditCardValidated);
});

export const getZipCodeGeoCoordinates: GlobalState => GeoCoordinates = createSelector(getUserData, userData => {
    return userData.zipCodeGeoCoordinates;
});

export const getAuthNuid: GlobalState => string = createSelector(stateSelector, state => state.authNuid || '');

export const getBidderId: GlobalState => number = createSelector(
    [getAuthNuid, userDataSelector],
    (authNuid, userData) => {
        if (!userData.bidderId) {
            const bidderIdMatches = authNuid.match(/(^[\d]+):::/);
            if (bidderIdMatches && bidderIdMatches.length > 1) {
                return Number(bidderIdMatches[1]);
            }
            return -1;
        }

        return userData.bidderId;
    },
);

export const isBidderLoaded: GlobalState => boolean = createSelector(getBidderId, bidderId => bidderId > 0);

export const isProfileComplete: GlobalState => boolean = createSelector(userDataSelector, userData => {
    return userData ? Boolean(userData.profileComplete) : false;
});

const getLoadTimeForUserData: GlobalState => number = createSelector(stateSelector, state => state.loaded || 0);

export const isUserDataLoading: GlobalState => boolean = createSelector(stateSelector, state => state.loading || false);

export const getUserDataLoaded: GlobalState => UserData = createSelector(
    stateSelector,
    state => state.loaded > 0 || false,
);

export const getSessionId: () => string = () => `${cookie.get('_session_id') || uuid.v4()}`;

const shouldFetchUserData = (state: GlobalState) => {
    const userAtLeastPartiallyRegistered = isUserAtLeastPartiallyRegistered(state);
    if (!userAtLeastPartiallyRegistered) return false;

    const userData = getUserData(state);
    if (userData) {
        const loaded = getLoadTimeForUserData(state);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isUserDataLoading(state);
    return !loading;
};

export const getMainhostLogin: GlobalState => MainhostLogin = createSelector(stateSelector, state => {
    return {
        authName: state.authName,
        authNuid: state.authNuid,
        authVerify: state.authVerify,
    };
});

/* ACTION CREATORS */
export const logOutBidder = () => ({
    type: LOG_OUT_BIDDER,
});

const loadBidderData = ({ authToken, deployment }) => async dispatch => {
    try {
        dispatch({
            payload: authToken,
            type: LOAD_BIDDER_DATA_REQUEST,
        });
        const response = await fetchBidderData({ authToken, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_BIDDER_DATA_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { token: authToken },
            payload: error,
            type: LOAD_BIDDER_DATA_FAIL,
        });
        dispatch(logOutBidder());
    }
};

export const fetchBidderDataIfNeeded = (token: ?string, force: boolean) => async (
    dispatch: Function,
    getState: Function,
) => {
    const state = getState();
    const authToken = token || getAuthToken(state);
    const deployment = getDeployment(state);
    if ((force && Boolean(authToken)) || shouldFetchUserData(state)) {
        return Promise.all([dispatch(loadBidderData({ authToken, deployment }))]);
    }
    return Promise.resolve();
};
